import ApiClient from 'api-client/ApiClient'
import ActionsSet from 'core/actions/ActionsSet'
import CreateAction from 'core/actions/CreateAction'
import DeleteAction from 'core/actions/DeleteAction'
import ListAction from 'core/actions/ListAction'
import UpdateAction from 'core/actions/UpdateAction'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import Bugsnag from 'utils/bugsnag'
import { pathStr } from 'utils/fp'
import { trackEvent } from 'utils/tracking'

const { capi } = ApiClient.getInstance()

export const persistentVolumeClaimActions = ActionsSet.make<DataKeys.PersistentVolumeClaims>({
  uniqueIdentifier: 'id',
  indexBy: 'clusterId',
  entityName: entityNamesByKey[DataKeys.PersistentVolumeClaims],
  cacheKey: DataKeys.PersistentVolumeClaims,
})

export const listPersistentVolumeClaims = persistentVolumeClaimActions.add(
  new ListAction<DataKeys.PersistentVolumeClaims, { clusterId: string }>(async (params) => {
    Bugsnag.leaveBreadcrumb('Attempting to get Persistent Volume Claims', params)
    return capi.getPersistentVolumeClaims(params.clusterId)
  })
    .addDependency(DataKeys.StorageClasses)
    .addDependency(DataKeys.PersistentVolumes),
)

export const createPersistentVolumeClaim = persistentVolumeClaimActions.add(
  new CreateAction<
    DataKeys.PersistentVolumeClaims,
    { clusterId: string; namespace: string; body: any }
  >(async ({ clusterId, namespace, body }) => {
    Bugsnag.leaveBreadcrumb('Attempting to create new persistent volume claim', {
      clusterId,
      namespace,
    })
    const created = await capi.createPersistentVolumeClaim(clusterId, namespace, body)
    trackEvent('Create New Persistent Volume Claim', {
      clusterId,
      namespace,
      name: pathStr('metadata.name', created),
    })
    return created
  }),
)

export const updatePersistentVolumeClaim = persistentVolumeClaimActions.add(
  new UpdateAction<
    DataKeys.PersistentVolumeClaims,
    {
      clusterId: string
      namespace: string
      name: string
      body: unknown
      requestType: string
      contentType?: string
    }
  >(async ({ clusterId, namespace, name, body, requestType = 'put', contentType }) => {
    Bugsnag.leaveBreadcrumb('Attempting to update persistent volume claim', {
      clusterId,
      namespace,
      name,
      body,
    })
    const updateFn =
      requestType === 'patch' ? capi.patchPersistentVolumeClaim : capi.updatePersistentVolumeClaim
    const updatedPersistentVolumeClaim = await updateFn({
      clusterId,
      namespace,
      name,
      body,
      contentType,
    })
    trackEvent('Update Persistent Volume Claim', { clusterId, namespace, name })
    return updatedPersistentVolumeClaim
  }),
)

export const deletePersistentVolumeClaim = persistentVolumeClaimActions.add(
  new DeleteAction<
    DataKeys.PersistentVolumeClaims,
    { clusterId: string; namespace: string; name: string; id: string }
  >(async ({ clusterId, namespace, name, id }) => {
    Bugsnag.leaveBreadcrumb('Attempting to delete persistent volume claim', {
      id,
      clusterId,
      namespace,
      name,
    })
    await capi.deletePersistentVolumeClaim(clusterId, namespace, name)
    trackEvent('Delete Persistent Volume Claim', { clusterId, namespace, name, id })
  }),
)
